'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Props {
  count: number;
}

// Backend integration point: replace with /api/properties/search?...params
const searchResults = [
{ id: 'sr-001', title: 'Saadiyat Beach Villa 6BR', location: 'Saadiyat Island, Abu Dhabi', price: 18500000, beds: 6, baths: 7, area: 9800, type: 'Villa', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1931ab245-1772165657979.png", imageAlt: 'Luxury beachfront villa with infinity pool on Saadiyat Island Abu Dhabi', badges: ['luxury', 'verified'], yield: 4.2, aiScore: 8.4, pricePerSqft: 1888 },
{ id: 'sr-002', title: 'Palm Signature Villa 7BR', location: 'Palm Jumeirah, Dubai', price: 32000000, beds: 7, baths: 8, area: 14200, type: 'Villa', image: "https://images.unsplash.com/photo-1504881626390-908d3e66fd73", imageAlt: 'White luxury villa on Palm Jumeirah with private beach access in Dubai', badges: ['luxury', 'featured', 'verified'], yield: 3.8, aiScore: 9.1, pricePerSqft: 2254 },
{ id: 'sr-003', title: 'Dubai Marina Penthouse 4BR', location: 'Dubai Marina, Dubai', price: 12500000, beds: 4, baths: 5, area: 5400, type: 'Penthouse', image: "https://img.rocket.new/generatedImages/rocket_gen_img_185c10fcd-1771894187850.png", imageAlt: 'Glass penthouse overlooking Dubai Marina with open plan interior', badges: ['featured', 'verified'], yield: 5.1, aiScore: 7.8, pricePerSqft: 2315 },
{ id: 'sr-004', title: 'Yas Bay Waterfront 2BR', location: 'Yas Island, Abu Dhabi', price: 2950000, beds: 2, baths: 3, area: 1480, type: 'Apartment', image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b477bb8-1772210228680.png", imageAlt: 'Modern waterfront apartment with balcony on Yas Island Abu Dhabi', badges: ['new', 'verified'], yield: 6.2, aiScore: 7.2, pricePerSqft: 1993 },
{ id: 'sr-005', title: 'Downtown Dubai Sky Tower 3BR', location: 'Downtown Dubai, Dubai', price: 4200000, beds: 3, baths: 4, area: 2100, type: 'Apartment', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png", imageAlt: 'Luxury apartment with panoramic Burj Khalifa view in Downtown Dubai', badges: ['featured', 'verified'], yield: 5.8, aiScore: 8.0, pricePerSqft: 2000 },
{ id: 'sr-006', title: 'Jumeirah Beach Townhouse 4BR', location: 'Jumeirah, Dubai', price: 7800000, beds: 4, baths: 5, area: 3900, type: 'Townhouse', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b196cdd6-1772210225382.png", imageAlt: 'Modern townhouse with private garden and beach access in Jumeirah Dubai', badges: ['luxury', 'verified'], yield: 4.5, aiScore: 7.5, pricePerSqft: 2000 },
{ id: 'sr-007', title: 'Dubai Hills Villa 4BR', location: 'Dubai Hills Estate, Dubai', price: 5800000, beds: 4, baths: 5, area: 3800, type: 'Villa', image: "https://img.rocket.new/generatedImages/rocket_gen_img_13dc422ac-1772210226514.png", imageAlt: 'Contemporary villa with landscaped garden in Dubai Hills Estate', badges: ['new', 'verified'], yield: 5.0, aiScore: 7.6, pricePerSqft: 1526 },
{ id: 'sr-008', title: 'Al Reem Sky Residence 1BR', location: 'Al Reem Island, Abu Dhabi', price: 1850000, beds: 1, baths: 2, area: 920, type: 'Apartment', image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b477bb8-1772210228680.png", imageAlt: 'Compact modern apartment with city views on Al Reem Island Abu Dhabi', badges: ['verified'], yield: 7.1, aiScore: 7.4, pricePerSqft: 2011 }];


function formatPrice(price: number) {
  if (price >= 1000000) return `AED ${(price / 1000000).toFixed(1)}M`;
  return `AED ${(price / 1000).toFixed(0)}K`;
}

const badgeMap: Record<string, {label: string;className: string;}> = {
  luxury: { label: 'Luxury', className: 'badge-luxury' },
  verified: { label: '✓ Verified', className: 'badge-verified' },
  featured: { label: 'Featured', className: 'badge-featured' },
  new: { label: 'New', className: 'badge-new' }
};

export default function SearchResults({ count }: Props) {
  const [sortBy, setSortBy] = useState('relevance');
  const [saved, setSaved] = useState<Set<string>>(new Set());

  const displayed = searchResults.slice(0, Math.min(count, searchResults.length));

  const sorted = [...displayed].sort((a, b) => {
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    if (sortBy === 'yield-desc') return b.yield - a.yield;
    if (sortBy === 'ai-score') return b.aiScore - a.aiScore;
    return 0;
  });

  return (
    <div>
      {/* Results Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            <span className="text-primary font-tabular">{count}</span> Properties Found
          </h2>
          <p className="text-muted-foreground text-sm mt-0.5">Matching your search criteria across the UAE</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-border rounded-xl px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
            
            <option value="relevance">Most Relevant</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="yield-desc">Highest Yield</option>
            <option value="ai-score">AI Score</option>
          </select>
          <Link
            href="/property-listings-page"
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
            
            <Icon name="MapIcon" size={16} />
            Map View
          </Link>
        </div>
      </div>

      {/* AI Search Banner */}
      <div className="bg-primary/8 border border-primary/20 rounded-2xl p-4 mb-6 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon name="SparklesIcon" size={16} className="text-primary" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">OROW AI Search Active</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            AI is analyzing {count} properties against your criteria. Results ranked by investment potential, fair price score, and lifestyle match.
          </p>
        </div>
        <button className="text-primary text-xs font-semibold hover:underline flex-shrink-0">
          AI Explain
        </button>
      </div>

      {/* Results Grid */}
      {sorted.length === 0 ?
      <div className="flex flex-col items-center justify-center py-24 text-center bg-card border border-border rounded-2xl">
          <Icon name="MagnifyingGlassIcon" size={48} className="text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-bold text-foreground mb-2">No properties match your filters</h3>
          <p className="text-muted-foreground text-sm mb-6 max-w-sm">Try broadening your search criteria — adjust price range, location, or property type.</p>
          <button className="btn-primary">Reset All Filters</button>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-5">
          {sorted.map((prop) =>
        <Link key={prop.id} href="/property-details-page" className="card-base group block">
              <div className="relative overflow-hidden h-52">
                <AppImage
              src={prop.image}
              alt={prop.imageAlt}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw" />
            
                <div className="absolute inset-0 bg-card-overlay" />

                {/* Badges */}
                <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                  {prop.badges.slice(0, 2).map((b) =>
              <span key={`sr-${prop.id}-badge-${b}`} className={badgeMap[b]?.className ?? 'badge-verified'}>
                      {badgeMap[b]?.label ?? b}
                    </span>
              )}
                </div>

                {/* Save */}
                <button
              onClick={(e) => {
                e.preventDefault();
                setSaved((prev) => {
                  const next = new Set(prev);
                  if (next.has(prop.id)) next.delete(prop.id);else
                  next.add(prop.id);
                  return next;
                });
              }}
              className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
              saved.has(prop.id) ? 'bg-accent text-white' : 'bg-black/30 text-white hover:bg-black/50'}`
              }
              aria-label="Save property">
              
                  <Icon name="HeartIcon" size={14} />
                </button>

                {/* Price */}
                <div className="absolute bottom-3 left-3">
                  <div className="text-white font-extrabold text-lg font-tabular">{formatPrice(prop.price)}</div>
                  <div className="text-white/70 text-xs">AED {prop.pricePerSqft.toLocaleString()}/sqft</div>
                </div>

                {/* AI Score */}
                <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1">
                  <div className="bg-primary/90 text-white text-xs font-bold px-2 py-0.5 rounded-lg">
                    {prop.yield}% Yield
                  </div>
                  <div className="bg-accent/90 text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-lg">
                    AI {prop.aiScore}/10
                  </div>
                </div>
              </div>

              <div className="p-4">
                <h3 className="font-bold text-foreground text-sm mb-1 line-clamp-1">{prop.title}</h3>
                <p className="text-muted-foreground text-xs mb-3 flex items-center gap-1">
                  <Icon name="MapPinIcon" size={12} />{prop.location}
                </p>

                <div className="flex items-center gap-3 text-xs text-foreground/80 mb-3 pb-3 border-b border-border">
                  <span className="font-medium">{prop.beds > 0 ? `${prop.beds} Beds` : 'Studio'}</span>
                  <span className="text-border">|</span>
                  <span className="font-medium">{prop.baths} Baths</span>
                  <span className="text-border">|</span>
                  <span className="font-medium">{prop.area.toLocaleString()} sqft</span>
                  <span className="text-border">|</span>
                  <span className="font-medium text-muted-foreground">{prop.type}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                  onClick={(e) => e.preventDefault()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary-dark transition-all active:scale-95">
                  
                      <Icon name="PhoneIcon" size={12} />
                      Call
                    </button>
                    <button
                  onClick={(e) => e.preventDefault()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-all active:scale-95">
                  
                      <Icon name="ChatBubbleLeftIcon" size={12} />
                      WhatsApp
                    </button>
                  </div>
                  <button
                onClick={(e) => e.preventDefault()}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                
                    <Icon name="ScaleIcon" size={13} />
                    Compare
                  </button>
                </div>
              </div>
            </Link>
        )}
        </div>
      }

      {/* Load More */}
      {sorted.length > 0 &&
      <div className="mt-8 text-center">
          <button className="btn-outline inline-flex items-center gap-2">
            <Icon name="ArrowPathIcon" size={16} />
            Load More Properties
          </button>
          <p className="text-muted-foreground text-xs mt-3">
            Showing {sorted.length} of {count} properties. Refine filters to narrow results.
          </p>
        </div>
      }
    </div>);

}